import { z } from 'zod';

/**
 * Password complexity policy returned by `GET /user/v1/password-policy`.
 * Drives the live requirements checklist on the onboard and reset-password
 * forms. Every field is a minimum count (0 = "rule not enforced"), except
 * `maxLength` which is an upper bound (0 = "no maximum"). Mirrors the
 * user-service `PasswordPolicyDTO` and the server-side
 * `PasswordUtils.ValidatePassword` rules the endpoints enforce. Wire shape:
 *   { length, maxLength, lowerCase, upperCase, digits, specialChars }.
 *
 * Single source of truth for the type — `features/auth/types.ts` re-exports it.
 */
export interface PasswordPolicy {
  /** Minimum total length. */
  length: number;
  /** Maximum total length (0 = unbounded). */
  maxLength: number;
  /** Minimum count of lowercase letters. */
  lowerCase: number;
  /** Minimum count of uppercase letters. */
  upperCase: number;
  /** Minimum count of digits. */
  digits: number;
  /** Minimum count of non-alphanumeric characters. */
  specialChars: number;
}

export type PasswordRuleId = keyof PasswordPolicy;

/**
 * Minimal translate signature — avoids importing i18next's heavy TFunction.
 * Resolves the `passwordRules.*` keys (ICU plural on `count`) in the `auth`
 * namespace.
 */
export type TranslateFn = (key: string, options?: { count?: number }) => string;

/** Counts non-overlapping matches of `pattern` (must be a global regex). */
export const countMatches = (pattern: RegExp, str: string): number => (str.match(pattern) ?? []).length;

export interface PasswordRequirement {
  id: PasswordRuleId;
  isValid: boolean;
}

/**
 * Evaluate each policy requirement against a password. Mirrors the legacy
 * PasswordRequirements component's `requirements` array (same ids and pass
 * conditions) so the live checklist behaves identically. Labels are an i18n/UI
 * concern and are supplied by the consumer (see `PasswordRequirements`).
 * Always returns all six rules; the consumer owns the zero-threshold guard.
 */
export function evaluateRequirements(password: string, policy: PasswordPolicy | undefined): PasswordRequirement[] {
  return [
    { id: 'length', isValid: password.length >= (policy?.length || 0) },
    { id: 'maxLength', isValid: policy?.maxLength ? password.length <= policy.maxLength : true },
    { id: 'upperCase', isValid: countMatches(/[A-Z]/g, password) >= (policy?.upperCase || 0) },
    { id: 'lowerCase', isValid: countMatches(/[a-z]/g, password) >= (policy?.lowerCase || 0) },
    { id: 'digits', isValid: countMatches(/[0-9]/g, password) >= (policy?.digits || 0) },
    { id: 'specialChars', isValid: countMatches(/[^a-zA-Z0-9]/g, password) >= (policy?.specialChars || 0) },
  ];
}

/**
 * Build the zod password schema from the policy. Direct port of legacy
 * `createPasswordValidationSchema` (Yup → zod): required, then min/max length
 * and per-class minimum counts, each gated on the policy actually specifying a
 * non-zero threshold (an absent/zero rule is skipped, exactly as the legacy
 * `if (safePolicy?.x)` guards do). Messages come from i18n via `t` — the
 * `passwordRules.*` keys (ICU plural on `count`), shared with the checklist.
 */
export function buildPasswordSchema(policy: PasswordPolicy | undefined, t: TranslateFn): z.ZodType<string> {
  const p = policy ?? ({} as Partial<PasswordPolicy>);
  const rule = (id: PasswordRuleId, count: number): string => t(`passwordRules.${id}`, { count });
  return z.string().superRefine((value, ctx) => {
    if (!value) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('passwordRules.required') });
      return;
    }
    if (p.length && value.length < p.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule('length', p.length) });
    if (p.maxLength && value.length > p.maxLength) ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule('maxLength', p.maxLength) });
    if (p.upperCase && countMatches(/[A-Z]/g, value) < p.upperCase) ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule('upperCase', p.upperCase) });
    if (p.lowerCase && countMatches(/[a-z]/g, value) < p.lowerCase) ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule('lowerCase', p.lowerCase) });
    if (p.digits && countMatches(/[0-9]/g, value) < p.digits) ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule('digits', p.digits) });
    if (p.specialChars && countMatches(/[^a-zA-Z0-9]/g, value) < p.specialChars)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule('specialChars', p.specialChars) });
  });
}
